/**
 * ChatMessage — renders a single message bubble in the AI Assistant chat thread.
 *
 * Supports:
 *   - user messages (right-aligned, brand-brown background)
 *   - assistant messages (left-aligned, paper background) with react-markdown
 *   - inline MUI Alert for `error` event payloads
 *   - ConfirmActionCard for `confirm_required` events (message.confirm set)
 *
 * The assistant now answers in synthesized Markdown prose; raw `data` event
 * table rendering has been removed. The model may include Markdown tables in
 * its text — those are rendered by react-markdown with contained horizontal
 * scroll so they never widen the panel.
 */

import React from 'react';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ConfirmActionCard, { ConfirmPayload } from './ConfirmActionCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DataPayload {
  columns: string[];
  rows: unknown[][];
}

export interface ChatMessageData {
  id: string;
  role: 'user' | 'assistant';
  /** Streaming or final text (markdown for assistant, plain for user). */
  text: string;
  /** If the message carries a structured data table, it lives here. */
  data?: DataPayload;
  /** If the message is an error bulletin rather than normal text. */
  isError?: boolean;
  /** If the message is a pending write-action confirmation card. */
  confirm?: ConfirmPayload;
  /** Server-side AiMessage id — present once the turn finishes; enables feedback. */
  messageId?: string;
  /** Feedback already given on this answer (1 helpful / -1 not). */
  rating?: 1 | -1;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ChatMessageProps {
  message: ChatMessageData;
  onConfirm?: (actionId: string) => void;
  onCancel?: (actionId: string) => void;
  onRate?: (messageId: string, rating: 1 | -1) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onConfirm, onCancel, onRate }) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
        px: 0.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          minWidth: '60px',
        }}
      >
        {/* Confirm-action card for `confirm_required` events */}
        {message.confirm ? (
          <ConfirmActionCard
            confirm={message.confirm}
            onConfirm={onConfirm ? () => onConfirm(message.confirm!.actionId) : undefined}
            onCancel={onCancel ? () => onCancel(message.confirm!.actionId) : undefined}
          />
        ) : message.isError ? (
          <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }}>
            {message.text}
          </Alert>
        ) : (
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              backgroundColor: isUser ? '#8B4513' : 'background.paper',
              border: isUser ? 'none' : '1px solid',
              borderColor: isUser ? 'transparent' : 'divider',
              boxShadow: isUser
                ? '0 2px 8px rgba(139,69,19,0.25)'
                : '0 1px 4px rgba(0,0,0,0.08)',
              color: isUser ? '#fff' : 'text.primary',

              // Markdown resets — keep prose tight inside the bubble
              '& p': { margin: 0, marginBottom: '0.4em' },
              '& p:last-child': { marginBottom: 0 },
              '& ul, & ol': { marginTop: '0.3em', marginBottom: '0.3em', paddingLeft: '1.4em' },
              '& code': {
                fontSize: '0.8em',
                backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : 'action.hover',
                borderRadius: '3px',
                padding: '0 3px',
              },
              '& pre': {
                backgroundColor: isUser ? 'rgba(255,255,255,0.1)' : 'action.hover',
                borderRadius: '6px',
                padding: '8px',
                overflowX: 'auto',
              },
              // Markdown tables — compact, full bubble width, horizontal scroll
              // contained inside the table block only (never widens the panel).
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: '0.8rem',
                display: 'block',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
              },
              '& th, & td': {
                border: isUser
                  ? '1px solid rgba(255,255,255,0.3)'
                  : '1px solid',
                borderColor: isUser ? undefined : 'divider',
                padding: '4px 8px',
                textAlign: 'left',
                whiteSpace: 'nowrap',
              },
              '& th': {
                fontWeight: 600,
                backgroundColor: isUser
                  ? 'rgba(255,255,255,0.15)'
                  : 'action.hover',
              },
            }}
          >
            {isUser ? (
              <Typography variant="body2" sx={{ color: '#fff', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.text}
              </Typography>
            ) : (
              <Box
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  '& > *:first-of-type': { marginTop: 0 },
                  '& > *:last-child': { marginBottom: 0 },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text || '​'}</ReactMarkdown>
              </Box>
            )}

          </Box>
        )}

        {/* Answer feedback — only on a finished assistant answer that the
            backend gave us an id for. Deliberately quiet: low-contrast icons
            below the bubble, replaced by a short thank-you once used. */}
        {!isUser && !message.isError && !message.confirm && message.messageId && onRate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, ml: 0.5 }}>
            {message.rating ? (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {message.rating === 1 ? 'Thanks for the feedback' : 'Thanks — we’ll use this to improve'}
              </Typography>
            ) : (
              <>
                <Tooltip title="This was helpful">
                  <IconButton
                    size="small"
                    aria-label="Mark answer as helpful"
                    onClick={() => onRate(message.messageId!, 1)}
                    sx={{ color: 'text.disabled', p: 0.25, '&:hover': { color: 'success.main' } }}
                  >
                    <ThumbUpOffAltIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="This wasn’t helpful">
                  <IconButton
                    size="small"
                    aria-label="Mark answer as not helpful"
                    onClick={() => onRate(message.messageId!, -1)}
                    sx={{ color: 'text.disabled', p: 0.25, '&:hover': { color: 'error.main' } }}
                  >
                    <ThumbDownOffAltIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
